'use client'

import { CategoryFaq } from '@/services/dataTypes'
import { HelpCircle, ChevronDown } from 'lucide-react'
import Link from 'next/link'
import React, { useState } from 'react'

function AccordionItem({ faq, isOpen, onToggle }: {
    faq: CategoryFaq; index: number; isOpen: boolean; onToggle: () => void
}) {
    return (
        <div
            className={`rounded-2xl border overflow-hidden transition-all duration-200 ${isOpen
                ? 'border-[#00d69f] shadow-xs'
                : 'border-slate-200 hover:border-slate-300'
            }`}
            style={{ background: '#FFFFFF' }}
            itemScope itemProp="mainEntity" itemType="https://schema.org/Question"
        >
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between px-5 py-4 text-left bg-transparent border-none cursor-pointer transition-all hover:bg-slate-50"
                aria-expanded={isOpen}
            >
                <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${isOpen ? 'bg-[#071822] text-white' : 'bg-slate-100 text-slate-600'}`}>
                        <HelpCircle size={12} />
                    </div>
                    <span
                        className={`text-xs sm:text-sm font-bold transition-colors ${isOpen ? 'text-[#00a87a]' : 'text-slate-800'}`}
                        itemProp="name"
                    >
                        {faq.question}
                    </span>
                </div>
                <ChevronDown
                    size={15}
                    className="text-slate-400 flex-shrink-0 transition-transform duration-200"
                    style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
            </button>

            {/* Answer */}
            <div
                className={`grid transition-[grid-template-rows,opacity] duration-200 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer"
            >
                <div className="overflow-hidden">
                    <div
                        className="px-5 pb-4 pt-2 text-xs sm:text-sm text-slate-800 border-t border-slate-100 leading-relaxed bg-slate-50/50"
                        itemProp="text"
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                    />
                </div>
            </div>
        </div>
    )
}

const CategoryFAQs = ({ faqs, categoryName, heading, text }: { faqs: CategoryFaq[]; categoryName?: string; heading?: string; text?: string }) => {
    const [openIdx, setOpenIdx] = useState<number | null>(0)

    if (!faqs?.length) return null

    return (
        <section className="bg-white py-14 px-4 sm:px-6 lg:px-8 border-t border-slate-100"
            aria-labelledby="t12-cat-faq-heading" itemScope itemType="https://schema.org/FAQPage">
            <div className="max-w-[1200px] mx-auto">

                {/* ── Header ── */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                    <div className="space-y-1.5">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] font-black uppercase tracking-wider">
                            <HelpCircle size={11} className="text-emerald-700" />
                            <span>{categoryName ? `${categoryName} FAQ` : 'FAQ'}</span>
                        </div>
                        <h2 id="t12-cat-faq-heading" className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                            {heading || (
                                <>
                                    Frequently Asked{' '}
                                    <span className="text-[#00a87a]">
                                        Questions
                                    </span>
                                </>
                            )}
                        </h2>
                        <p className="text-slate-800 text-xs sm:text-sm font-medium">{text || 'Everything you need to know about saving in this category.'}</p>
                    </div>
                    <Link href="/contact-us"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all flex-shrink-0 bg-slate-50 hover:bg-[#071822] text-slate-700 hover:text-white border border-slate-200"
                    >
                        <span>Need Help? Contact Us</span>
                    </Link>
                </div>

                {/* ── Accordion List ── */}
                <div className="space-y-2.5 max-w-4xl">
                    {faqs.map((faq, index) => (
                        <AccordionItem
                            key={faq.id || index}
                            faq={faq}
                            index={index}
                            isOpen={openIdx === index}
                            onToggle={() => setOpenIdx(openIdx === index ? null : index)}
                        />
                    ))}
                </div>
            </div>
        </section>
    )
}

export default CategoryFAQs
